import React from 'react'
import _ from 'lodash'
import createClass from 'create-react-class'
import ValidationMixin from 'live/components/forms/mixins/validation-mixin'
import FormGroup from 'live/components/forms/ui/form-group'
import Select from 'live/components/forms/ui/select'
import EncryptForm from 'live/components/forms/ui/UserTable/EncryptForm'
import PasswordInput from 'live/components/forms/ui/UserTable/PasswordInput'
import {
    encryptDidMount,
    handleEncryptInitPassword,
    handleEncryptSubmit
} from 'live/components/forms/ui/UserTable/utils'
import { FormEnabledContext } from 'live/context/form-enabled'
import i18n from 'live/services/i18n'
import globalNotification from 'live/services/global-notification'
import { Input, RadioGroupCard } from '@intelie/ui'
import styled from 'styled-components'
import { CustomSwitch } from './components/CustomSwitch'

const PASSWORD_MASK = 'PASSWORD'

const DEFAULT_WRITE_CONCERN_PARAMS = {
    w: '1', // typeof 'string' | number >= 0
    journal: false,
    wTimeoutMS: 0 // in milliseconds
}
const DEFAULT_COUNT_CACHE = 60 * 1000
const DEFAULT_STATISTICS_CACHE = 60 * 1000 * 10
const DEFAULT_COLLECTION_COMPRESSION = 'snappy'

const reset = function (config) {
    config.username = ''
    config.password = ''
    config.addresses = []
    config.dbName = 'live'
    config.authenticationDatabase = ''
    config.authenticationMechanism = ''
    config.replicaSet = true
    config.useSnappyCompressor = false
    config.useZlibCompressor = false
    config.readPreference = 'primary'
    config.ssl = false
    config.writeConcernParameters = DEFAULT_WRITE_CONCERN_PARAMS
}

// eslint-disable-next-line @typescript-eslint/no-var-requires
const ObjectInitializerMixin = require('live/components/forms/mixins/object-initializer')('/rest/extension', {
    type: 'mongodb',
    active: true,
    config: {
        // mongodb connection
        uri: '',
        addresses: ['localhost:27017'],
        dbName: 'live',
        authenticationDatabase: '',
        authenticationMechanism: '',
        replicaSet: true,
        useSnappyCompressor: false,
        useZlibCompressor: false,
        readPreference: 'primary',
        ssl: false,
        prefix: 'mongodb://',
        // plugin features
        enableForQuery: true,
        enableForStorage: true,
        ensureIndexes: true,
        ensureSharding: false,
        readOnly: false,
        nonIndexable: false,
        filter: '',
        asyncCount: true,
        shouldUseRangeFieldRule: false,
        countCacheDuration: DEFAULT_COUNT_CACHE,
        collectionCompression: DEFAULT_COLLECTION_COMPRESSION,
        statisticsCacheDuration: DEFAULT_STATISTICS_CACHE,
        writeConcernParameters: DEFAULT_WRITE_CONCERN_PARAMS
    },
    fullQualifier: null,
    qualifier: null,
    status: {}
})

const validations = {
    properties: {
        config: {
            properties: {
                dbName: {
                    required: false,
                    allowEmpty: false,
                    message: i18n('The database name cannot be empty')
                },
                addresses: {
                    required: false,
                    type: 'array',
                    conform: function (addresses) {
                        const addressesErrors = this.getAddressErrors(addresses)

                        return addressesErrors.length === 0
                    },
                    message: i18n('address may be non-empty once connection string is not set')
                },
                uri: {
                    required: false,
                    type: 'string',
                    allowEmpty: true,
                    conform: function (uri) {
                        return typeof uri === 'string' && uri === ''
                            ? true
                            : uri.startsWith('mongodb://') || uri.startsWith('mongodb+srv://')
                    },
                    message: i18n('URI connection string must start by mongodb:// or mongodb+srv://')
                },
                writeConcernParameters: {
                    required: false,
                    conform: function (writeConcernParameters) {
                        const writeConcernErrors = this.getWriteConcernErrors(writeConcernParameters)

                        return writeConcernErrors.length === 0
                    },
                    message: i18n('Error in write concern configuration')
                }
            }
        }
    }
}

const ConnectionOptionsEnum = { LEGACY: 'legacy', URI: 'uri' }

const MongoForm = createClass({
    mixins: [ObjectInitializerMixin, ValidationMixin],
    validations,
    getInitialState: function () {
        return {
            mongodb: {
                validations: {
                    addresses: [],
                    writeConcernParameters: []
                }
            },
            connectionMethodology: ConnectionOptionsEnum.LEGACY,
            connectionStringDetailsState: 'default'
        }
    },
    onToggleSwitch: function (field) {
        const object = this.state.object
        const config = object.config

        config[field] = !config[field]
        this.applyObj({ config: config })
        this.handleValidation({ target: { name: field, value: config[field] } })
    },
    componentDidMount: function () {
        encryptDidMount.bind(this)()
        const validationConfigs = ['addresses', 'writeConcernParameters']
        validationConfigs.forEach(config => {
            validations.properties.config.properties[config].conform = validations.properties.config.properties[
                config
            ].conform.bind(this)
        })
    },
    componentDidUpdate: function (prevProps, prevState) {
        handleEncryptInitPassword.bind(this)(prevState)

        const errors = this.state.errors
        const addressErrorsTrack = errors.filter(error => error.property === 'config.addresses')

        if (addressErrorsTrack.length !== 0) {
            const object = this.state.object
            const config = object.config

            const addressesErrors = this.getAddressErrors(config.addresses)
            this.validateAndSaveConfigField(config, 'addresses', addressesErrors)
        }

        const writeConcernErrorsTrack = errors.filter(error => error.property === 'config.writeConcernParameters')

        if (writeConcernErrorsTrack.length !== 0) {
            const object = this.state.object
            const config = object.config

            const writeConcernErrors = this.getWriteConcernErrors(config.writeConcernParameters)
            this.validateAndSaveConfigField(config, 'writeConcernParameters', writeConcernErrors)
        }

        const config = this.state.object.config
        const jsonUri = config.uri

        if (jsonUri) {
            const parsedJsonUri = this.parseConnectionUri(jsonUri)
            const uriFromJsonUri = this.composeConnectionUri(
                parsedJsonUri.prefix,
                parsedJsonUri.username,
                parsedJsonUri.password,
                parsedJsonUri.connectionStringDetails
            )
            const uriFromInputs = this.composeConnectionUri(
                config.prefix,
                config.username,
                config.password,
                config.connectionStringDetails
            )

            if (uriFromJsonUri !== uriFromInputs) {
                this.updateConfigFromParsedJsonUri(parsedJsonUri)
            }
        }
    },
    onChangeAddress: function (address, i) {
        const object = this.state.object
        const config = object.config

        config.addresses[i] = address

        const addressesErrors = this.getAddressErrors(config.addresses)

        this.validateAndSaveConfigField(config, 'addresses', addressesErrors)
    },
    onAddAddress: function () {
        const object = this.state.object
        const config = object.config
        const addresses = config.addresses
        const addressesErrors = this.getAddressErrors(addresses)

        addresses.push(``)
        config.addresses = addresses

        this.validateAndSaveConfigField(config, 'addresses', addressesErrors)
    },
    onRemoveAddress: function (i) {
        const object = this.state.object
        const config = object.config
        const addresses = config.addresses

        if (config.addresses.length === 1) {
            addresses[0] = ``
        } else {
            addresses.splice(i, 1)
        }

        const addressesErrors = this.getAddressErrors(addresses)

        this.validateAndSaveConfigField(config, 'addresses', addressesErrors)
    },
    getAddressErrors: function (addresses) {
        const propertyName = 'addresses'
        const errors = []

        for (const [index, address] of Object.entries(addresses)) {
            if (address === undefined || address.trim() === '') {
                const addressError = {
                    actual: address,
                    message: i18n('address at position %{index} cannot be empty', { index: Number(index) + 1 }),
                    property: `${propertyName}.${index}`
                }
                errors.push(addressError)
            }
        }

        return errors
    },
    onChangeReadPreference: function (value) {
        const object = this.state.object
        const config = object.config

        config.readPreference = value
        this.applyObj({ config: config })
        this.validateProperty('config.readPreference', value)
    },
    onChangeCollectionCompression: function (value) {
        const object = this.state.object
        const config = object.config

        config.collectionCompression = value
        this.applyObj({ config: config })
        this.validateProperty('config.collectionCompression', value)
    },
    onChangeConnectionUri: function (value) {
        const object = this.state.object
        const config = object.config

        config.addresses = [] // fixup
        config.uri = value
        this.applyObj({ config: config })
        this.validateProperty('config.uri', value, true)
    },
    updateConnectionUri: function (config) {
        const connectionUri = this.composeConnectionUri(
            config.prefix,
            config.username,
            config.password,
            config.connectionStringDetails
        )

        this.onChangeConnectionUri(connectionUri)
    },
    onChangePrefixConnectionFormat: function (value) {
        const object = this.state.object
        const config = object.config

        config.prefix = value
        this.applyObj({ config: config })
        this.validateProperty('config.prefix', value)
        this.updateConnectionUri(config)
    },
    onChangeConnectionStringDetails: function (value) {
        const object = this.state.object
        const config = object.config
        const parsedValue = this.parseConnectionUri(value)
        const newConnectionStringDetails = parsedValue.connectionStringDetails
        const connectionStringDetailsState =
            parsedValue.prefix !== '' || parsedValue.username !== '' || parsedValue.password !== ''
                ? 'warning'
                : 'default'

        config.connectionStringDetails = newConnectionStringDetails
        this.applyObj({ config: config, connectionStringDetailsState: connectionStringDetailsState })
        this.validateProperty('config.connectionStringDetails', newConnectionStringDetails)
        this.updateConnectionUri(config)
    },
    onChangeUsername: function (value) {
        const object = this.state.object
        const config = object.config

        config.username = value
        this.applyObj({ config: config })
        this.updateConnectionUri(config)
    },
    onChangePassword: function (value) {
        const object = this.state.object
        const config = object.config

        config.password = value
        this.applyObj({ config: config })
        this.updateConnectionUri(config)
    },
    onChangeSelectConnectionMethodology: function (value) {
        const object = this.state.object
        const config = _.cloneDeep(object.config)
        const connectionMethodology = this.state.connectionMethodology

        if (connectionMethodology === ConnectionOptionsEnum.LEGACY && value === ConnectionOptionsEnum.URI) {
            const options = []

            if (config.authenticationDatabase && config.authenticationDatabase !== '') {
                options.push('authSource=' + config.authenticationDatabase)
            }
            if (config.readPreference && config.readPreference !== '') {
                options.push('readPreference=' + config.readPreference)
            }
            if (config.authenticationMechanism && config.authenticationMechanism !== '') {
                options.push('authMechanism=' + config.authenticationMechanism)
            }
            if (config.replicaSet) options.push('replicaSet=live')
            if (config.writeConcernParameters) {
                if (config.writeConcernParameters.w != null) {
                    options.push('w=' + config.writeConcernParameters.w)
                }
                if (config.writeConcernParameters.wTimeoutMS != null) {
                    options.push('wtimeoutMS=' + config.writeConcernParameters.wTimeoutMS)
                }
                if (config.writeConcernParameters.journal != null) {
                    options.push('journal=' + config.writeConcernParameters.journal)
                }
            }

            const compressors = []
            if (config.useSnappyCompressor) compressors.push('snappy')
            if (config.useZlibCompressor) compressors.push('zlib')

            if (compressors.length > 0) options.push('compressors=' + compressors.join(','))
            if (config.ssl) options.push('ssl=true')

            const params = options.filter(e => e !== '').join('&')
            const PREFIX_CONNECTION_DEFAULT = 'mongodb://'
            const connectionStringDetails =
                config.addresses.join(',') + '/' + config.dbName + (params !== '' ? '?' + params : '')
            config.uri = this.composeConnectionUri(
                PREFIX_CONNECTION_DEFAULT,
                config.username, // TODO: if username or password contains $ : / ? # [ ] @ we must escape using %
                config.password, // TODO: problem here about LIVE ENCRYPTED PASSWORDS
                connectionStringDetails
            )

            config.addresses = [] // fixup
            this.onChangePrefixConnectionFormat(PREFIX_CONNECTION_DEFAULT)
            this.onChangeConnectionStringDetails(connectionStringDetails)
            this.onChangeConnectionUri(config.uri)
        }

        if (connectionMethodology === ConnectionOptionsEnum.URI && value === ConnectionOptionsEnum.LEGACY) {
            const fragments = config.uri?.split('?') || []
            if (fragments.length > 0) {
                // reset to the new standard mongodb connection string semantics
                reset(config)

                // parsing basic fragment
                const location = fragments[0].substring(fragments[0].indexOf('://') + 3) + ''
                if (location.includes('@')) {
                    const regex = '(.*):(.*)@(.*)/'
                    const matches = location.match(regex)
                    config.username = decodeURIComponent(matches[1])
                    config.password = decodeURIComponent(matches[2])
                    const servers = matches[3]
                    const maybeDatabases = servers.split('/')
                    config.addresses = maybeDatabases[0].split(',') || []
                    if (maybeDatabases.length > 1) {
                        config.dbName = maybeDatabases[1]
                    }
                }
            }

            config.writeConcernParameters = DEFAULT_WRITE_CONCERN_PARAMS // fixup

            // parsing additional parameters fragment
            if (fragments.length === 2) {
                fragments[1].split('&').forEach(opt => {
                    const arry = opt.split('=')
                    const key = arry[0]
                    const value = arry[1]

                    switch (key?.toLowerCase()) {
                        case 'authsource': {
                            config.authenticationDatabase = value
                            break
                        }
                        case 'readpreference': {
                            config.readPreference = value
                            break
                        }
                        case 'authmechanism': {
                            config.authenticationMechanism = value
                            break
                        }
                        case 'replicaset': {
                            config.replicaSet = true
                            break
                        }
                        case 'compressors': {
                            if (value === 'snappy') config.useSnappyCompressor = true
                            if (value === 'zlib') config.useZlibCompressor = true
                            break
                        }
                        case 'ssl': {
                            config.ssl = value === 'true'
                            break
                        }
                        case 'w': {
                            config.writeConcernParameters.w = value
                            break
                        }
                        case 'wtimeoutms': {
                            const parsed = parseInt(value)
                            if (isIntegerEqualOrGreaterThanZero(parsed)) {
                                config.writeConcernParameters.wTimeoutMS = parsed
                            }
                            break
                        }
                        case 'journal': {
                            config.writeConcernParameters.journal = value === 'true'
                            break
                        }
                        default: {
                            break
                        }
                    }
                })
            }

            config.uri = '' // fixup
            this.applyObj({ config })
        }

        this.setState({ connectionMethodology: value, mustSave: true })
    },
    _onSuccess: function () {
        window.location.href = '#/integrations/' + this.state.object.type + '/' + this.state.object.id
    },
    _onAfterFetch: function () {
        const object = this.state.object
        const config = object.config

        const connectionMethodology =
            !config.uri || config.uri === '' ? ConnectionOptionsEnum.LEGACY : ConnectionOptionsEnum.URI
        this.setState({ connectionMethodology })

        if (!config.addresses) {
            config.addresses = []
            this.applyObj({ config })
        }
    },
    _onDelete: function () {
        window.location.href = '#/integrations/'
    },
    replacePasswordWithPlaceholder: function (uri) {
        const parsedUri = this.parseConnectionUri(uri)

        const uriWithMaskedPassword = this.composeConnectionUri(
            parsedUri.prefix,
            parsedUri.username,
            parsedUri.password ? PASSWORD_MASK : parsedUri.password,
            parsedUri.connectionStringDetails
        )

        return uriWithMaskedPassword
    },
    composeConnectionUri: function (prefix, username, password, connectionStringDetails) {
        let auth = ''
        if (username && password) {
            auth = (encodeURIComponent(username) || '') + ':' + (encodeURIComponent(password) || '') + '@'
        }

        const connectionUri = prefix + auth + connectionStringDetails

        return connectionUri
    },
    updateConfigFromParsedJsonUri: function (parsedJsonUri) {
        this.setState({
            object: {
                ...this.state.object,
                config: {
                    ...this.state.object.config,
                    prefix: parsedJsonUri.prefix,
                    username: parsedJsonUri.username || '',
                    password: parsedJsonUri.password || '',
                    connectionStringDetails: parsedJsonUri.connectionStringDetails
                }
            }
        })
    },
    parseConnectionUri: function (uri) {
        let auxUri = uri

        const prefixRegex = /^(mongodb(?:\+srv)?:\/\/)/
        const prefixMatch = uri.match(prefixRegex)
        const prefix = prefixMatch ? prefixMatch[1] : ''
        auxUri = auxUri.replace(prefix, '')

        if (auxUri.includes('@')) {
            const lastIndex = auxUri.lastIndexOf('@')

            const connectionStringDetails = auxUri.substring(lastIndex + 1)
            const auth = auxUri.substring(0, lastIndex)
            const [username, password] = auth.split(':')

            return {
                prefix,
                username: decodeURIComponent(username),
                password: decodeURIComponent(password),
                connectionStringDetails
            }
        } else {
            const connectionStringDetails = auxUri

            return {
                prefix,
                username: '',
                password: '',
                connectionStringDetails
            }
        }
    },
    onChangeItemWriteConcernParam: function (key, value) {
        const object = this.state.object
        const config = object.config
        const writeConcernParameters = config.writeConcernParameters

        writeConcernParameters[key] = value
        this.onChangeWriteConcernParameters(writeConcernParameters)
    },
    onChangeWriteConcernParameters: function (writeConcernParameters) {
        const object = this.state.object
        const config = object.config

        const writeConcernErrors = this.getWriteConcernErrors(writeConcernParameters)

        this.validateAndSaveConfigField(config, 'writeConcernParameters', writeConcernErrors)
    },
    getWriteConcernErrors: function (writeConcernParameters) {
        const propertyName = 'writeConcernParameters'
        const errors = []

        for (const [key, value] of Object.entries(writeConcernParameters)) {
            if (key === 'w') {
                if (typeof value !== 'string' && !isIntegerEqualOrGreaterThanZero(value)) {
                    const levelError = {
                        actual: value,
                        message: i18n('W parameter must be a valid string (ex: majority or a tag) or a integer >= 0'),
                        property: `${propertyName}.${key}`
                    }

                    errors.push(levelError)
                }
            }

            if (key === 'wTimeoutMS') {
                if (!isIntegerEqualOrGreaterThanZero(value)) {
                    const timeoutError = {
                        actual: value,
                        message: i18n('wTimeoutMS parameter must be a integer >= 0'),
                        property: `${propertyName}.${key}`
                    }

                    errors.push(timeoutError)
                }
            }
        }

        return errors
    },
    validateAndSaveConfigField: function (config, configKey, configErrors) {
        const otherErrors = this.state.errors.filter(error => error.property !== `config.${configKey}`)

        const allErrors = [...otherErrors, ...configErrors]

        const mongodb = {
            validations: {
                ...this.state.mongodb.validations,
                [configKey]: configErrors
            }
        }

        if (this.state.validationActive) {
            this.setState({
                object: _.assign(this.state.object, { config }),
                mustSave: true,
                errors: [...otherErrors],
                valid: allErrors.length === 0,
                mongodb
            })
        } else {
            this.setState({
                object: _.assign(this.state.object, { config }),
                mustSave: true
            })
        }
    },
    getPropertyState: function (configKey, propertyKey) {
        const mongodbState = this.state.mongodb
        const validations = mongodbState.validations
        const configValidation = validations[configKey]

        const errorsFound = configValidation.filter(error => error.property === `${configKey}.${propertyKey}`)

        return errorsFound.length === 0 ? 'success' : 'error'
    },
    getPropertyErrorReason: function (configKey, propertyKey) {
        const mongodbState = this.state.mongodb
        const validations = mongodbState.validations
        const configValidation = validations[configKey]

        const errorsFound = configValidation
            .filter(error => error.property === `${configKey}.${propertyKey}`)
            .map(error => error.message)

        return errorsFound.join(', ')
    },
    // eslint-disable-next-line react/display-name
    render: function () {
        const object = this.state.object
        const config = object.config
        const { readonly } = this.context // LIVE view admin permission
        const viewOnlyAdmin = readonly === true
        const connectionMethodology = this.state.connectionMethodology
        handleEncryptSubmit.bind(this)()

        if (!config.addresses) {
            config.addresses = []
        }

        if (!config.writeConcernParameters) {
            config.writeConcernParameters = DEFAULT_WRITE_CONCERN_PARAMS
        }

        if (this.state.errors.length > 0) {
            console.error(
                'Failure on MongoDB plugin configuration, see validation errors=',
                this.state.errors,
                'config resolved=',
                this.state.config
            )
            const messages = []
            this.state.errors.forEach(err => {
                messages.push(
                    i18n('Property %{property} is invalid due %{reason}', {
                        property: err.property,
                        reason: err.message
                    })
                )
            })
            globalNotification.error(
                <span>
                    {i18n('Some configuration parameters are invalid')}
                    <br />
                    {messages.join('\n')}
                </span>
            )
        }

        const uriInputState = this.errorsFor('config.uri') === '' ? 'default' : 'error'

        return (
            <EncryptForm self={this}>
                <div className="form-area">
                    <div className="form-group">
                        <RadioGroupCard
                            name="radio-group"
                            label={<h3>{i18n('Connection Method')}</h3>}
                            readOnly={viewOnlyAdmin}
                            onChange={this.onChangeSelectConnectionMethodology}
                            selectedOption={connectionMethodology}
                            options={[
                                {
                                    label: i18n('Connection URI'),
                                    description: i18n(
                                        'Enter the connection string manually to handle all MongoDB native features supported.'
                                    ),
                                    parentheses: i18n('recommended'),
                                    value: 'uri'
                                },
                                {
                                    label: i18n('Driver properties'),
                                    description: i18n(
                                        'Assisted configuration of a few driver options supported by older versions of this plugin. Only available for migration purpose.'
                                    ),
                                    parentheses: i18n('deprecated'),
                                    value: 'legacy'
                                }
                            ]}
                        />
                    </div>
                    {connectionMethodology === 'uri' ? (
                        <div className="tables form-group">
                            <hr />
                            <h3 className="subtitle-label">{i18n('MongoDB connection')}</h3>

                            <div className="form-group">
                                <Select
                                    disabled={viewOnlyAdmin}
                                    label={i18n('Connection Format Prefix')}
                                    value={config.prefix}
                                    options={[
                                        { value: 'mongodb://', label: 'mongodb://' },
                                        { value: 'mongodb+srv://', label: 'mongodb+srv://' }
                                    ]}
                                    onChange={this.onChangePrefixConnectionFormat}
                                    clearable={false}
                                />

                                <FormGroup
                                    disabled={viewOnlyAdmin}
                                    type="text"
                                    label={i18n('Username')}
                                    name="config.username"
                                    value={config.username}
                                    onChange={e => this.onChangeUsername(e.target.value)}
                                    validationState={this.validationState('config.username')}
                                    hasFeedback
                                    help={this.errorsFor('config.username')}
                                />

                                <PasswordInput
                                    id={`${object?.type}/${object?.qualifier || ''}`}
                                    label={i18n('Password')}
                                    name="config.password"
                                    initPassword={this.state.initPassword}
                                    onChange={newPassword => this.onChangePassword(newPassword)}
                                    setEditing={this.state.setHasUnSavedPassword}
                                    hasFeedback
                                    help={this.errorsFor('config.password')}
                                    hidden={viewOnlyAdmin}
                                />

                                <Input.TextArea
                                    disabled={viewOnlyAdmin}
                                    label={i18n('Connection String Details')}
                                    onChange={e => this.onChangeConnectionStringDetails(e.target.value)}
                                    required={true}
                                    resizable={true}
                                    value={config.connectionStringDetails}
                                    inputState={this.state.object.connectionStringDetailsState}
                                    infoText={
                                        this.state.object.connectionStringDetailsState === 'warning'
                                            ? i18n(
                                                  'Do not paste the complete URI connection string; only connection string details should be considered in this text area.'
                                              )
                                            : ''
                                    }
                                />
                            </div>

                            <PreviewUriDiv>
                                <h4>{i18n('Connection URI')}</h4>
                                <p>{this.replacePasswordWithPlaceholder(config.uri)}</p>
                                <span>
                                    {uriInputState === 'error'
                                        ? i18n('The connection string must start by mongodb:// or mongodb+srv://') +
                                          '. '
                                        : null}
                                    <i className="far fa-info-circle" />
                                    {i18n('Check the expected syntax on ')}
                                    <a
                                        href="https://www.mongodb.com/docs/manual/reference/connection-string"
                                        target="_blank"
                                    >
                                        {i18n('MongoDB Connection String')}
                                    </a>
                                    {'. '}
                                </span>
                            </PreviewUriDiv>
                        </div>
                    ) : (
                        <div className="form-group">
                            <hr />
                            <h3>{i18n('MongoDB driver parameters')}</h3>

                            <div className="form-group">
                                <label>{i18n('Addresses')}</label>
                                {config.addresses.map((address, i) => (
                                    <FormGroup
                                        key={`${object?.fullQualifier}.${i}`}
                                        type="text"
                                        className="form-mongodb-address"
                                        name="config.addresses"
                                        value={address}
                                        hasFeedback
                                        validationState={this.getPropertyState('addresses', i)}
                                        help={this.getPropertyErrorReason('addresses', i)}
                                        onChange={e => this.onChangeAddress(e.target.value, i)}
                                        addonAfter={
                                            <a onClick={() => this.onRemoveAddress(i)} className="delete-address">
                                                <i className="far fa-trash-alt fa-fw" />
                                            </a>
                                        }
                                    />
                                ))}
                                <button
                                    className="button-mongodb-add-address btn btn-primary"
                                    disabled={viewOnlyAdmin}
                                    onClick={this.onAddAddress}
                                >
                                    <span>
                                        <i className="far fa-plus-circle" />
                                    </span>
                                    <span className="text-add-address-button">{i18n('ADD NEW ADDRESS')}</span>
                                </button>
                            </div>

                            <CustomSwitch
                                disabled={viewOnlyAdmin}
                                active={config.ssl}
                                onChange={() => {
                                    this.onToggleSwitch('ssl')
                                }}
                                description={i18n('SSL')}
                            />

                            <CustomSwitch
                                disabled={viewOnlyAdmin}
                                active={config.replicaSet}
                                onChange={() => {
                                    this.onToggleSwitch('replicaSet')
                                }}
                                description={i18n('Replica Set')}
                                help={i18n(
                                    'By default, this integration will use replica set named as "live" due compatibility with previous versions of INTELIE MongoDB plugin. If you want to change, please use Connection URI instead of Driver parameters.'
                                )}
                            />

                            <CustomSwitch
                                disabled={viewOnlyAdmin}
                                active={config.useSnappyCompressor}
                                onChange={() => {
                                    this.onToggleSwitch('useSnappyCompressor')
                                }}
                                description={i18n('Snappy compression')}
                                help={i18n('Used with server versions 3.4 and greater')}
                            />

                            <CustomSwitch
                                disabled={viewOnlyAdmin}
                                active={config.useZlibCompressor}
                                onChange={() => {
                                    this.onToggleSwitch('useZlibCompressor')
                                }}
                                description={i18n('Zlib compression')}
                                help={i18n('Used with server versions 3.6 and greater')}
                            />

                            <FormGroup
                                type="text"
                                label={i18n('Database')}
                                name="config.dbName"
                                value={config.dbName}
                                onChange={this.handleValidation}
                                validationState={this.validationState('config.dbName')}
                                hasFeedback
                                help={this.errorsFor('config.dbName')}
                            />

                            <FormGroup
                                type="text"
                                label={i18n('Authentication Database')}
                                name="config.authenticationDatabase"
                                value={config.authenticationDatabase}
                                onChange={this.handleValidation}
                                validationState={this.validationState('config.authenticationDatabase')}
                                hasFeedback
                                help={this.errorsFor('config.authenticationDatabase')}
                            />

                            <FormGroup
                                type="text"
                                label={i18n('Authentication Mechanism')}
                                name="config.authenticationMechanism"
                                value={config.authenticationMechanism}
                                onChange={this.handleValidation}
                                validationState={this.validationState('config.authenticationMechanism')}
                                hasFeedback
                                help={
                                    this.errorsFor('config.authenticationMechanism') ||
                                    i18n('If you left this field blank, the default is SCRAM-SHA-1 for MongoDB 3.6+')
                                }
                            />

                            <Select
                                label={i18n('Read Preference')}
                                value={config.readPreference}
                                options={[
                                    { value: 'primary', label: 'primary' },
                                    { value: 'primaryPreferred', label: 'primaryPreferred' },
                                    { value: 'secondary', label: 'secondary' },
                                    { value: 'secondaryPreferred', label: 'secondaryPreferred' },
                                    { value: 'nearest', label: 'nearest' }
                                ]}
                                onChange={this.onChangeReadPreference}
                                clearable={false}
                            />

                            <FormGroup
                                type="text"
                                label={i18n('Username')}
                                name="config.username"
                                value={config.username}
                                onChange={this.handleValidation}
                                validationState={this.validationState('config.username')}
                                hasFeedback
                                help={this.errorsFor('config.username')}
                            />

                            <PasswordInput
                                id={`${object?.type}/${object?.qualifier || ''}`}
                                label={i18n('Password')}
                                name="config.password"
                                initPassword={this.state.initPassword}
                                onChange={newPassword =>
                                    this.handleValidation({
                                        target: {
                                            name: 'config.password',
                                            value: newPassword
                                        }
                                    })
                                }
                                setEditing={this.state.setHasUnSavedPassword}
                                hasFeedback
                                help={this.errorsFor('config.password')}
                                hidden={viewOnlyAdmin}
                                style={{ marginBottom: '-20px' }}
                            />

                            <div className="form-group">
                                <hr />
                                <WriteConcernTitle className="form-group">
                                    <h3>{i18n('Write Concern Configuration')}</h3>
                                    <span>
                                        <i className="far fa-info-circle" />
                                        {`${i18n('For more information, check')} `}
                                        <a
                                            href="https://www.mongodb.com/docs/manual/reference/write-concern/"
                                            target="_blank"
                                        >
                                            {i18n('Write Concern Reference')}
                                        </a>
                                        {'.'}
                                    </span>
                                </WriteConcernTitle>

                                <FormGroup
                                    type="text"
                                    label={i18n('W')}
                                    name="config.writeConcernParameters.w"
                                    value={config.writeConcernParameters.w}
                                    onChange={event => {
                                        this.onChangeItemWriteConcernParam('w', event.target.value)
                                    }}
                                    validationState={this.getPropertyState('writeConcernParameters', 'w')}
                                    hasFeedback
                                    help={
                                        this.getPropertyErrorReason('writeConcernParameters', 'w') ||
                                        i18n(
                                            'Requests acknowledgement that the write operation has propagated to a specified number of mongod instances or to mongod instances with specified tags.'
                                        )
                                    }
                                />

                                <CustomSwitch
                                    disabled={viewOnlyAdmin}
                                    description={i18n('Journal')}
                                    active={!!config.writeConcernParameters?.journal}
                                    onChange={() => {
                                        this.onChangeItemWriteConcernParam(
                                            'journal',
                                            !config.writeConcernParameters?.journal
                                        )
                                    }}
                                    help={i18n(
                                        'Requests acknowledgement from MongoDB that the write operation has been written to the journal.'
                                    )}
                                />

                                <FormGroup
                                    name="config.writeConcernParameters.wTimeoutMS"
                                    type="number"
                                    label={i18n('wTimeout')}
                                    inputSuffix={i18n('milliseconds')}
                                    value={
                                        config.writeConcernParameters.wTimeoutMS ??
                                        DEFAULT_WRITE_CONCERN_PARAMS.wTimeoutMS
                                    }
                                    onChange={event => {
                                        this.onChangeItemWriteConcernParam('wTimeoutMS', event.target.value)
                                    }}
                                    validationState={this.getPropertyState('writeConcernParameters', 'wTimeoutMS')}
                                    hasFeedback
                                    help={
                                        this.getPropertyErrorReason('writeConcernParameters', 'wTimeoutMS') ||
                                        i18n(
                                            'wTimeout causes write operations to return with an error after the specified limit, even if the required write concern will eventually succeed. If you do not specify the wtimeout option and the level of write concern is unachievable, the write operation will block indefinitely. Specifying a wtimeout value of 0 is equivalent to a write concern without the wtimeout option.'
                                        )
                                    }
                                />
                            </div>
                        </div>
                    )}

                    {!config.enableForStorage ? null : (
                        <div className="form-group">
                            <hr />
                            <h3>{i18n('Plugin features')}</h3>
                            <CustomSwitch
                                disabled={viewOnlyAdmin}
                                active={config.readOnly}
                                onChange={() => {
                                    this.onToggleSwitch('readOnly')
                                }}
                                description={i18n('Read-only')}
                                help={i18n(
                                    'If enabled, makes this integration only for reads and it will not register the storage provider as writable in Live runtime.'
                                )}
                            />

                            <CustomSwitch
                                disabled={viewOnlyAdmin}
                                active={config.nonIndexable}
                                onChange={() => {
                                    this.onToggleSwitch('nonIndexable')
                                }}
                                description={i18n('Non-indexable')}
                                help={i18n(
                                    'If enabled, makes this integration to ignore any index creation requests coming from the Live runtime.'
                                )}
                            />

                            <CustomSwitch
                                disabled={viewOnlyAdmin || config.nonIndexable}
                                active={config.shouldUseRangeFieldRule && !config.nonIndexable}
                                onChange={() => {
                                    this.onToggleSwitch('shouldUseRangeFieldRule')
                                }}
                                description={i18n('MongoDB_InstanceForm_FollowRangeFieldRule_Switch_Label')}
                                help={i18n('MongoDB_InstanceForm_FollowRangeFieldRule_Switch_Tooltip')}
                            />

                            <CustomSwitch
                                disabled={viewOnlyAdmin || config.nonIndexable}
                                active={config.ensureIndexes}
                                onChange={() => {
                                    this.onToggleSwitch('ensureIndexes')
                                }}
                                description={i18n('Create default timestamp index')}
                                help={i18n(
                                    'If enabled, it creates automatically the default index for the timestamp field in all collections. Recommended: keep it enabled.'
                                )}
                            />

                            <CustomSwitch
                                disabled={viewOnlyAdmin}
                                active={config.ensureSharding}
                                onChange={() => {
                                    this.onToggleSwitch('ensureSharding')
                                }}
                                description={i18n('Create default timestamp shard key')}
                                help={i18n(
                                    'If enabled, it creates automatically the shard key for the default timestamp criteria. Recommended: only enable if the default timestamp shard is useful for your setup.'
                                )}
                            />

                            <CustomSwitch
                                disabled={viewOnlyAdmin}
                                active={config.asyncCount}
                                onChange={() => {
                                    this.onToggleSwitch('asyncCount')
                                }}
                                description={i18n('Asynchronous counting')}
                                help={i18n(
                                    'If enabled, it prevents blocking the event reads by allowing the event iterator count operation to complete asynchronously. It may produce inaccurate results for the effective total of events for the query while it computes.'
                                )}
                            />

                            <FormGroup
                                name="config.countCacheDuration"
                                type="number"
                                label={i18n('Count cache eviction time (per storage query)')}
                                inputSuffix={i18n('milliseconds')}
                                value={config.countCacheDuration ?? DEFAULT_COUNT_CACHE}
                                onChange={this.handleValidation}
                                validationState={this.validationState('config.countCacheDuration')}
                                help={i18n(
                                    'Besides the asynchronous counting, this integration is able to keep in cache the total of documents already seen by repeated queries. Write and delete operations will expire all count cache kept for previous queries.'
                                )}
                                hasFeedback
                            />

                            <Select
                                label={i18n('Collection compression')}
                                value={config.collectionCompression ?? DEFAULT_COLLECTION_COMPRESSION}
                                options={[
                                    { value: 'snappy', label: 'snappy' },
                                    { value: 'zlib', label: 'zlib' },
                                    { value: 'zstd', label: 'zstd' }
                                ]}
                                onChange={this.onChangeCollectionCompression}
                                clearable={false}
                            />

                            <FormGroup
                                name="config.statisticsCacheDuration"
                                type="number"
                                label={i18n('Statistics cache eviction time')}
                                inputSuffix={i18n('milliseconds')}
                                value={config.statisticsCacheDuration ?? DEFAULT_STATISTICS_CACHE}
                                onChange={this.handleValidation}
                                validationState={this.validationState('config.statisticsCacheDuration')}
                                help={i18n(
                                    'INTELIE Live runtime ask Storage Providers every 30 minutes for event type metrics, at least. The metrics subsystem also can ask fresh results dynamically by Pipes functions. This plugin keeps a cache for every MongoDB collection stats to prevent the extra load against the storage servers. Additionally, the cache also expires once deletions are performed.'
                                )}
                                hasFeedback
                            />

                            <FormGroup
                                disabled={viewOnlyAdmin || config.readOnly}
                                type="text"
                                label={i18n('Write filter')}
                                name="config.filter"
                                value={config.filter}
                                onChange={this.handleValidation}
                                validationState={this.validationState('config.filter')}
                                hasFeedback
                                help={
                                    this.errorsFor('config.filter') || (
                                        <span>
                                            {i18n(
                                                "This expression will control which data can be written into this integration. It doesn't prevent the reads or index management neither. You can use both event fields or just event types wildcards."
                                            )}{' '}
                                            {`${i18n('For more information, check')} `}
                                            <a
                                                href="https://pipes.intelie.com/docs/0.25.8/#quickstart-filters"
                                                target="_blank"
                                            >
                                                {i18n('INTELIE Pipes Filter syntax')}
                                            </a>
                                            {'.'}
                                        </span>
                                    )
                                }
                                addonAfter={
                                    <span>
                                        {i18n(
                                            'The filter must be in Pipes format. Leave the box empty for "no filter".'
                                        )}
                                    </span>
                                }
                            />
                        </div>
                    )}
                </div>
            </EncryptForm>
        )
    }
})

const PreviewUriDiv = styled.div`
    > h4 {
        margin-bottom: 4px;
    }
    > span {
        font-size: 12px;
        color: var(--gray-700);
        > i {
            font-size: 10px;
            margin-right: 4px;
        }
    }
`

const WriteConcernTitle = styled.div`
    > h3 {
        margin-bottom: 4px;
    }
    > span {
        font-size: 12px;
        color: var(--gray-700);
        > i {
            font-size: 10px;
            margin-right: 4px;
        }
    }
`

const isIntegerEqualOrGreaterThanZero = function (value) {
    return !isNaN(value) && Number.isInteger(parseFloat(value)) && parseFloat(value) >= 0
}

MongoForm.contextType = FormEnabledContext
export default MongoForm
